// src/pages/AIPage.jsx
import React, { useState, useContext } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { UserContext } from '../context/UserContext';
import { ThemeContext } from '../context/ThemeContext';
import aiService from '../services/ai';

const AIPage = () => {
  const { userType, aiSearchesUsed, incrementAISearch } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isFreeUser = userType === 'free';
  const maxSearches = isFreeUser ? 5 : Infinity;
  const searchesLeft = maxSearches - aiSearchesUsed;

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (isFreeUser && aiSearchesUsed >= maxSearches) {
      setError('Monthly AI search limit reached. Upgrade to Pro for unlimited searches.');
      return;
    }

    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await aiService.askAI(query);
      // Backend returns { status: 'success', data: { question, answer, tokensUsed, searchesRemaining } }
      const answer = res?.data?.answer || res?.answer || JSON.stringify(res);
      setResponse(answer);
      incrementAISearch();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to get AI response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`ai-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 w-full overflow-x-hidden">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Assistant</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Get AI-generated insights on any topic
              </p>
              
              {/* Usage Meter */}
              <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 dark:text-gray-300">AI Searches This Month</span>
                  <span className="font-bold">
                    {aiSearchesUsed} / {isFreeUser ? '5' : 'Unlimited'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full" 
                    style={{ width: `${(aiSearchesUsed / maxSearches) * 100}%` }}
                  ></div>
                </div>
                {isFreeUser && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {searchesLeft} searches remaining. Reset on the 1st of next month.
                  </p>
                )}
              </div>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask AI anything..."
                  className="w-full p-4 pl-12 pr-24 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading || (isFreeUser && aiSearchesUsed >= maxSearches)}
                />
                <div className="absolute left-4 top-4 text-gray-400">
                  🤖
                </div>
                <button
                  type="submit"
                  disabled={loading || (isFreeUser && aiSearchesUsed >= maxSearches)}
                  className="absolute right-2 top-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Thinking...' : 'Ask AI'}
                </button>
              </div>
              {error && (
                <div className="mt-2 p-3 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}
            </form>

            {/* Response Area */}
            {response && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">AI Response</h3>
                  <button 
                    onClick={() => navigator.clipboard.writeText(response)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Copy
                  </button>
                </div>
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{response}</p>
                </div>
              </div>
            )}

            {/* Examples */}
            <div className="mt-12">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Try asking:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Summarize my meeting notes from last week",
                  "Explain quantum physics in simple terms",
                  "What are the main topics in my programming notes?",
                  "Suggest healthy meal ideas for a week"
                ].map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setQuery(example)}
                    className="p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                  >
                    <p className="text-gray-700 dark:text-gray-300">{example}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AIPage;